/**
 * ContinuePassportCard — Home hero for a user's active Food Passport.
 * Shows the next unvisited stop with a big "Open Passport" CTA + Directions.
 * Falls back to the older commitments widget when the user has no Discovery goal joined.
 */
import { useQuery } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { MapPin, Navigation, ArrowRight, BookMarked } from "lucide-react";

interface PassportData {
  goalId: string;
  goalTitle: string;
  city: string;
  imageUrl: string | null;
  total: number;
  visited: number;
  required: number;
  joined: boolean;
  nextStop: {
    id: string;
    name: string;
    area: string | null;
    photoUrl: string | null;
    geoLat: number | null;
    geoLng: number | null;
  } | null;
}

function directionsHref(lat?: number | null, lng?: number | null, name?: string) {
  if (lat != null && lng != null) {
    return `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
  }
  const q = encodeURIComponent(name || "");
  return `https://www.google.com/maps/search/?api=1&query=${q}`;
}

export function ContinuePassportCard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["continue-passport", user?.id],
    staleTime: 60_000,
    queryFn: async (): Promise<PassportData | null> => {
      // 1. Try newest Discovery goal the user joined
      let goal: any = null;
      let joined = false;

      if (user?.id) {
        const { data: parts } = await supabase
          .from("common_goal_participants")
          .select(
            "common_goal_id, joined_at, common_goals!inner(id, title, category, image_url, goal_type, stops_required)"
          )
          .eq("user_id", user.id)
          .order("joined_at", { ascending: false });
        const discoveryPart = (parts || []).find(
          (r: any) => r.common_goals?.goal_type === "discovery"
        );
        if (discoveryPart) {
          goal = (discoveryPart as any).common_goals;
          joined = true;
        }
      }

      // 2. Fallback: newest public Discovery goal (so the passport is always surfaced)
      if (!goal) {
        const { data: g } = await supabase
          .from("common_goals")
          .select("id, title, category, image_url, goal_type, stops_required")
          .eq("goal_type" as any, "discovery")
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (!g) return null;
        goal = g;
      }

      const [stopsRes, visitsRes] = await Promise.all([
        supabase
          .from("discovery_stops" as any)
          .select("id, name, area, photo_url, geo_lat, geo_lng, display_order")
          .eq("goal_id", goal.id)
          .order("display_order", { ascending: true }),
        user?.id
          ? supabase
              .from("discovery_visits" as any)
              .select("stop_id")
              .eq("goal_id", goal.id)
              .eq("user_id", user.id)
          : Promise.resolve({ data: [] as any[] } as any),
      ]);

      const visitedIds = new Set(((visitsRes as any)?.data || []).map((v: any) => v.stop_id));
      const stopList = ((stopsRes as any)?.data || []) as any[];
      const next = stopList.find((s) => !visitedIds.has(s.id)) || stopList[0] || null;

      return {
        goalId: goal.id,
        goalTitle: goal.title,
        city: goal.category || "Abu Dhabi",
        imageUrl: goal.image_url,
        total: stopList.length,
        visited: stopList.filter((s) => visitedIds.has(s.id)).length,
        required: goal.stops_required ?? stopList.length,
        joined,
        nextStop: next
          ? {
              id: next.id,
              name: next.name,
              area: next.area,
              photoUrl: next.photo_url,
              geoLat: next.geo_lat,
              geoLng: next.geo_lng,
            }
          : null,
      };
    },
  });


  if (isLoading) return <Skeleton className="h-40 w-full rounded-2xl" />;
  if (!data) return null;

  const heroImg = data.nextStop?.photoUrl || data.imageUrl;
  const pct = data.required > 0 ? Math.min(100, Math.round((data.visited / data.required) * 100)) : 0;
  const done = data.nextStop == null;

  return (
    <div className="relative rounded-2xl overflow-hidden border shadow-md group">
      {/* Hero image */}
      <div className="relative aspect-[16/8] bg-muted overflow-hidden">
        {heroImg ? (
          <img
            src={heroImg}
            alt=""
            className="w-full h-full object-cover transition-transform group-hover:scale-105"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20" />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
        <div className="absolute top-3 left-3 inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-2.5 py-1 text-[11px] font-extrabold text-primary shadow">
          <BookMarked className="h-3.5 w-3.5" />
          Food Passport · {data.city}
        </div>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3 bg-card">
        <div>
          <p className="t-eyebrow">
            {done ? "Passport complete" : data.joined ? "Continue your passport" : "Start your passport"}
          </p>
          <h3 className="text-base font-extrabold leading-tight mt-0.5">
            {done
              ? data.goalTitle
              : data.joined
              ? `Next stop: ${data.nextStop?.name}`
              : data.goalTitle}
          </h3>

          {!done && data.nextStop?.area && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
              <MapPin className="h-3 w-3" /> {data.nextStop.area}
            </p>
          )}
        </div>

        <div className="flex items-center gap-3">
          <div className="flex-1">
            <Progress value={pct} className="h-2" />
          </div>
          <p className="text-xs font-bold whitespace-nowrap">
            {data.visited} <span className="text-muted-foreground font-normal">/ {data.required}</span>
          </p>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <Button
            onClick={() => navigate(`/community-goals/${data.goalId}`)}
            className="h-10 font-semibold"
          >
            {data.joined ? "Open Passport" : "Explore Passport"} <ArrowRight className="h-4 w-4 ml-1" />
          </Button>
          {!done && (
            <a
              href={directionsHref(data.nextStop?.geoLat, data.nextStop?.geoLng, data.nextStop?.name)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-1.5 h-10 rounded-md bg-muted hover:bg-accent text-sm font-semibold transition"
            >
              <Navigation className="h-4 w-4" /> Directions
            </a>
          )}
          {done && (
            <Link
              to={`/community-goals/${data.goalId}`}
              className="inline-flex items-center justify-center gap-1.5 h-10 rounded-md bg-emerald-50 dark:bg-emerald-950/30 text-emerald-800 dark:text-emerald-200 text-sm font-semibold hover:bg-emerald-100 transition"
            >
              View certificate
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
